import { AppIcon, AppText } from "@toothfairy/shared-ui";
import AppAssets from "@toothfairy/shared-ui/AppAssets";
import AppTheme from "@toothfairy/shared-ui/AppTheme";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { StyleSheet, View, Linking, Platform } from "react-native";
import { useReactiveVar } from "react-reactive-var";
import { isMobileScreen } from "../../Screens/MainViewContainer";
import useMobilePlatform from "../../Hooks/useMobilePlatform";
import envConfig from "../../../envConfig";

const APP_STORE_URL = `https://apps.apple.com/app/toothfairyai/id${envConfig.REACT_APP_IOS_APP_ID}`;
const PLAY_STORE_URL =
  "https://play.google.com/store/apps/details?id=com.mobile.toothfairyai";

const BANNER_DISMISSED_KEY = "appInstallBannerDismissedAt";

const AppInstallBanner = () => {
  const { currentTheme } = AppTheme.useTheme();
  const { t } = useTranslation();
  const isMobile = useReactiveVar(isMobileScreen);
  const platform = useMobilePlatform();
  const [isVisible, setIsVisible] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);

  useEffect(() => {
    if (!platform) return;

    if (platform === "ios") {
      const dismissedAt = localStorage.getItem(BANNER_DISMISSED_KEY);
      if (dismissedAt) {
        const daysSince =
          (Date.now() - parseInt(dismissedAt, 10)) / (1000 * 60 * 60 * 24);
        if (daysSince < 7) return;
      }
      setIsVisible(true);
      return;
    }

    if (platform === "android") {
      if (envConfig.REACT_APP_ANDROID_APP_ENABLED !== "true") return;

      const dismissedAt = localStorage.getItem(BANNER_DISMISSED_KEY);
      if (dismissedAt) {
        const daysSince =
          (Date.now() - parseInt(dismissedAt, 10)) / (1000 * 60 * 60 * 24);
        if (daysSince < 7) return;
      }

      const handleBeforeInstall = (e) => {
        e.preventDefault();
        setDeferredPrompt(e);
        setIsVisible(true);
      };

      window.addEventListener("beforeinstallprompt", handleBeforeInstall);
      setIsVisible(true);

      return () => {
        window.removeEventListener("beforeinstallprompt", handleBeforeInstall);
      };
    }
  }, [platform]);

  const handleInstall = async () => {
    if (platform === "ios") {
      Linking.openURL(APP_STORE_URL);
    } else if (platform === "android" && deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === "accepted") {
        setIsVisible(false);
      }
      setDeferredPrompt(null);
    } else if (platform === "android") {
      Linking.openURL(PLAY_STORE_URL);
    }
  };

  const handleDismiss = () => {
    setIsVisible(false);
    localStorage.setItem(BANNER_DISMISSED_KEY, Date.now().toString());
  };

  if (!platform || !isVisible) return null;

  const styles = generateStyles({ currentTheme, isMobile });

  return (
    <View style={styles.container}>
      <AppIcon
        isClickable
        icon={AppAssets.icons.close}
        size={14}
        color={currentTheme?.theme?.white}
        wrapperStyle={styles.closeButton}
        onPress={handleDismiss}
      />
      <AppText style={styles.text} color={currentTheme?.theme?.white}>
        {t("appInstallBanner.message", {
          platform: platform === "ios" ? "App Store" : "Google Play",
        })}
      </AppText>
      <AppText
        style={styles.installButton}
        color={currentTheme?.theme?.white}
        onPress={handleInstall}
      >
        {t("appInstallBanner.install")}
      </AppText>
    </View>
  );
};

const generateStyles = ({ currentTheme, isMobile }) =>
  StyleSheet.create({
    container: {
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "center",
      backgroundColor: currentTheme?.theme?.primary,
      paddingVertical: 5,
      gap: 7,
      paddingHorizontal: 8,
      borderBottomColor: currentTheme?.theme?.border_color,
      borderBottomWidth: 1,
      minHeight: 35,
      position: "absolute",
      zIndex: 1000,
      width: "100%",
      alignSelf: "center",
      borderBottomLeftRadius: 5,
      borderBottomRightRadius: 5,
      overflow: "hidden",
      appRegion: "no-drag",
      WebkitAppRegion: "no-drag",
    },
    text: {
      fontSize: 12,
      fontWeight: "500",
      flex: 1,
      flexShrink: 1,
      textAlign: "center",
      width: 200,
    },
    installButton: {
      fontSize: 12,
      fontWeight: "700",
      textDecorationLine: "underline",
      cursor: "pointer",
      flexShrink: 0,
    },
    closeButton: {
      left: 0,
      flexShrink: 0,
      appRegion: "no-drag",
      WebkitAppRegion: "no-drag",
    },
  });

export default AppInstallBanner;
